import { buildPaginationParams } from '../../pages/AdminPanel/BulkDealDocsUpload/utils';
import { MaggieHttp } from '../MaggieHttp';

export interface BulkUploadSummary {
  id: number;
  fileName: string;
  fileSize: string;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  totalFiles: number;
  processedFiles: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  uploadedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface BulkUploadDetail extends BulkUploadSummary {
  s3StagingKey: string;
  errorMessage: string | null;
}

export interface BulkUploadItem {
  id: number;
  bulkUploadId: number;
  filePath: string;
  fileName: string;
  fileSize: number;
  fundName: string | null;
  companyNameParsed: string | null;
  roundDateParsed: string | null;
  roundNameParsed: string | null;
  companyId: number | null;
  roundId: number | null;
  documentType: 'captable' | 'closing_doc' | null;
  status: 'pending' | 'processing' | 'success' | 'failed' | 'skipped';
  dealDocId: number | null;
  finalS3Key: string | null;
  errorMessage: string | null;
  errorType: 'company_not_found' | 'round_not_found' | 'invalid_file' | 'upload_failed' | 'other' | null;
  createdAt: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Get list of all bulk uploads with pagination
 */
export async function getBulkDealDocsUploads(
  page: number = 1,
  limit: number = 20,
  status?: string
): Promise<PaginatedResponse<BulkUploadSummary>> {
  try {
    const params = buildPaginationParams(page, limit, status);
    const response = await MaggieHttp.maggie.get<PaginatedResponse<BulkUploadSummary>>(
      '/round/documents/bulk',
      { params }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to load bulk uploads: ${error instanceof Error ? error.message : 'Unknown error'}`,
      { cause: error }
    );
  }
}

/**
 * Get details of a specific bulk upload
 */
export async function getBulkUploadDetail(uploadId: number): Promise<BulkUploadDetail> {
  try {
    const response = await MaggieHttp.maggie.get<BulkUploadDetail>(`/round/documents/bulk/${uploadId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to load upload details: ${error instanceof Error ? error.message : 'Unknown error'}`,
      { cause: error }
    );
  }
}

/**
 * Get items for a bulk upload with pagination
 */
export async function getBulkUploadItems(
  uploadId: number,
  page: number = 1,
  limit: number = 50,
  status?: string
): Promise<PaginatedResponse<BulkUploadItem>> {
  try {
    const params = buildPaginationParams(page, limit, status);
    const response = await MaggieHttp.maggie.get<PaginatedResponse<BulkUploadItem>>(
      `/round/documents/bulk/${uploadId}/items`,
      { params }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to load upload items: ${error instanceof Error ? error.message : 'Unknown error'}`,
      { cause: error }
    );
  }
}

/**
 * Request presigned URL for uploading ZIP file
 */
export async function requestBulkUploadUrl(
  fileName: string,
  fileSize: number
): Promise<{
  uploadUrl: string;
  s3Key: string;
}> {
  try {
    const response = await MaggieHttp.maggie.post<
      { fileName: string; fileSize: number },
      { uploadUrl: string; s3Key: string }
      // Part of the bulk deal-doc upload flow — no client timeout.
    >('/round/documents/bulk/request-upload', { fileName, fileSize }, { timeout: 0 });
    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to request upload URL: ${error instanceof Error ? error.message : 'Unknown error'}`,
      { cause: error }
    );
  }
}

/**
 * Confirm bulk upload after S3 upload complete
 */
export async function confirmBulkUpload(
  fileName: string,
  fileSize: number,
  s3Key: string
): Promise<{
  uploadId: number;
  status: string;
  message: string;
  totalFiles: number;
}> {
  try {
    const response = await MaggieHttp.maggie.post<
      { fileName: string; fileSize: number; s3Key: string },
      {
        uploadId: number;
        status: string;
        message: string;
        totalFiles: number;
      }
      // Enumerates the uploaded zip server-side — no client timeout.
    >('/round/documents/bulk/confirm', { fileName, fileSize, s3Key }, { timeout: 0 });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to confirm upload: ${error instanceof Error ? error.message : 'Unknown error'}`, {
      cause: error,
    });
  }
}

/**
 * Start processing bulk upload
 */
export async function startBulkUploadProcessing(uploadId: number): Promise<{ message: string }> {
  try {
    const response = await MaggieHttp.maggie.post<undefined, { message: string }>(
      `/round/documents/bulk/${uploadId}/process`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to start processing: ${error instanceof Error ? error.message : 'Unknown error'}`,
      { cause: error }
    );
  }
}
